import React, {useState, useEffect, useRef, useContext, useCallback} from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import OTPInputField from '../../../common/components/Inputs/OTPInputField';
import { verifyMFAWithSession } from '../../../../services/Users';
import { parseClaimsFromHeader, getUserIDCookie, getSessionTimerCookie, getSessionDeadlineCookie } from '../../../../utils/auth/ClaimsParser';
import WithActions from "../../../common/components/Wrappers/WithActions";
import ButtonPrimary from "../../../common/components/Buttons/ButtonPrimary";
import DisabledFormState from "../../../common/components/Wrappers/DisabledFormState";
import {ThemeContext} from "styled-components";

const VerifyMFAForm = ({
    formData,
    onChangeAuthForm,
    onLoginSuccess,
    onLoginError,
    onStepTransition,
    isLoading,
    setIsLoading,
    csrfToken,
    disabled = false,
    isVisible = true,
}) => {
    const [localError, setLocalError] = useState(null);
    const [mfaVerificationCode, setMfaVerificationCode] = useState('');
    const submissionIdRef = useRef(0);
    const lastSubmittedCodeRef = useRef(null);
    const theme = useContext(ThemeContext);

    const handleSubmit = useCallback(async (e) => {
        if (e?.preventDefault) {
            e.preventDefault();
        }

        // Prevent duplicate submissions
        if (isLoading || disabled || !isVisible || mfaVerificationCode.length !== 6) {
            return;
        }

        // Check if we've already submitted this exact code
        if (lastSubmittedCodeRef.current === mfaVerificationCode) {
            return;
        }

        // Validate required data
        if (!csrfToken) {
            setLocalError("Session expired. Please refresh the page and try again.");
            return;
        }

        if (!formData?.mfaVerificationData?.mfa_session_id || !formData?.mfaVerificationData?.ext_user_id || !formData?.email) {
            setLocalError("MFA session data is missing. Please try logging in again.");
            return;
        }

        setIsLoading(true);
        setLocalError(null);
        lastSubmittedCodeRef.current = mfaVerificationCode;

        try {
            const response = await verifyMFAWithSession(
                formData.email,
                mfaVerificationCode,
                formData.mfaVerificationData.mfa_session_id,
                formData.mfaVerificationData.ext_user_id,
                csrfToken
            );
            if (response.data?.error) {
                // Clear OTP input on error
                setMfaVerificationCode('');
                lastSubmittedCodeRef.current = null;
                onLoginError("Invalid MFA code. Please try again.");
                setIsLoading(false);
                return;
            }

            // Check if response indicates success (status 200-299)
            const isSuccessStatus = response?.status >= 200 && response?.status < 300;

            // Parse claims from header (for context/authorization)
            let claims = parseClaimsFromHeader(response);

            // Authentication is proven by cookies: UserID + SessionTimer + SessionDeadline
            // The x-user-context header is for authorization/context, not authentication
            if (isSuccessStatus) {
                // Try to get authentication cookies - wait briefly for them to be set
                for (let attempt = 1; attempt <= 3; attempt++) {
                    await new Promise(resolve => setTimeout(resolve, attempt * 100));

                    const userID = getUserIDCookie();
                    const sessionTimer = getSessionTimerCookie();
                    const sessionDeadline = getSessionDeadlineCookie();

                    // Authentication requires UserID, SessionTimer, and SessionDeadline
                    if (userID && sessionTimer && sessionDeadline) {
                        const authClaims = { user_sub: userID, email: formData.email };
                        await new Promise(resolve => setTimeout(resolve, 50));
                        onLoginSuccess(authClaims);
                        setIsLoading(false);
                        return;
                    }
                }

                // Fallback: If header is accessible and has user_sub, use it
                if (claims && claims.user_sub) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                    onLoginSuccess({ ...claims, email: formData.email });
                    setIsLoading(false);
                    return;
                }

                // Try response.data.claims as last resort
                if (response?.data?.claims) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                    onLoginSuccess({ ...response.data.claims, email: formData.email });
                    setIsLoading(false);
                    return;
                }

                onLoginError("Authentication succeeded but session information could not be retrieved. Please refresh the page.");
                setIsLoading(false);
                return;
            }

            // Fallback: If we have claims from header but no success status
            if (claims && claims.user_sub) {
                await new Promise(resolve => setTimeout(resolve, 50));
                onLoginSuccess({ ...claims, email: formData.email });
                setIsLoading(false);
                return;
            }

            // Try response.data.claims
            if (response?.data?.claims) {
                await new Promise(resolve => setTimeout(resolve, 50));
                onLoginSuccess({ ...response.data.claims, email: formData.email });
                setIsLoading(false);
                return;
            }

            onLoginError("Authentication failed. Please try again.");
            setIsLoading(false);
        } catch (err) {
            // Clear OTP input on any error
            setMfaVerificationCode('');
            lastSubmittedCodeRef.current = null;

            // A 401 on MFA verification typically means invalid code, not session expiration
            // Session expiration would happen earlier when validating CSRF token or MFA session
            // Check if error message indicates session/token issues vs MFA code issues
            const errorData = err.response?.data;
            const errorMessage = typeof errorData === 'string' ? errorData : errorData?.error || errorData?.message || '';
            const isSessionError = errorMessage.toLowerCase().includes('session') ||
                                 errorMessage.toLowerCase().includes('token') ||
                                 errorMessage.toLowerCase().includes('expired') ||
                                 errorMessage === 'token is invalid';

            if (err.response?.status === 401 && isSessionError) {
                // Only show session expired if the error explicitly mentions session/token
                const errorMsg = "Your session has expired. Please log in again.";
                onLoginError(errorMsg);
                setIsLoading(false);

                // Transition back to login step if callback is available
                if (onStepTransition) {
                    setTimeout(() => {
                        onStepTransition('login', errorMsg);
                    }, 500);
                }
            } else {
                // For MFA code errors (401 with invalid code, or other errors), show MFA error
                onLoginError("Invalid MFA code. Please try again.");
                setIsLoading(false);
            }
        }
    }, [mfaVerificationCode, isLoading, disabled, isVisible, csrfToken, formData?.mfaVerificationData?.mfa_session_id, formData?.mfaVerificationData?.ext_user_id, formData?.email, onLoginError, onLoginSuccess, onStepTransition, setIsLoading, setMfaVerificationCode]);

    // Auto-submit when code is complete (only if form is ready)
    useEffect(() => {
        const hasAlreadySubmittedThisCode = lastSubmittedCodeRef.current === mfaVerificationCode;

        if (
            mfaVerificationCode.length === 6 &&
            !isLoading &&
            !disabled &&
            isVisible &&
            csrfToken &&
            formData?.mfaVerificationData?.mfa_session_id &&
            formData?.mfaVerificationData?.ext_user_id &&
            formData?.email &&
            !hasAlreadySubmittedThisCode
        ) {
            handleSubmit({ preventDefault: () => {} });
        }

        // Clear the submitted code tracking when code changes
        if (mfaVerificationCode.length < 6 || mfaVerificationCode !== lastSubmittedCodeRef.current) {
            lastSubmittedCodeRef.current = null;
        }
    }, [mfaVerificationCode, isLoading, disabled, isVisible, csrfToken, formData?.mfaVerificationData?.mfa_session_id, formData?.mfaVerificationData?.ext_user_id, formData?.email, handleSubmit]);

    // Safety check for formData - must be after all hooks
    if (!formData || !isVisible) {
        return null;
    }

    if (disabled) {
        return <DisabledFormState message="Verified" tooltip="Added" />
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                margin: '8px 0',
                transition: 'all 0.3s ease'
            }}
        >
            <Box sx={{ mt: -1 }}>
                <WithActions
                    forceSpacing
                    inputType="text"
                >
                    <Typography
                        component="div"
                        sx={{
                            color: theme.colors.text,
                            textAlign: 'left',
                            width: '100%',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            margin: 0,
                            padding: 0,
                            lineHeight: '40px',
                            height: '40px',
                            display: 'block',
                        }}
                    >
                        Next MFA Code
                    </Typography>
                </WithActions>
            </Box>
            <br/>
            <Box
                sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
                data-keeper-disable="true"
                data-keeper-ignore="true"
                data-password-manager="disabled"
            >
                <div data-keeper-ignore="true" data-password-manager="disabled">
                    <WithActions
                        disableKeeper={true}
                        showRefreshButton={false}

                        // showSuccessButton={disabled}
                        isVisible={true}
                        inputType="code"
                        forceSpacing={true}
                        fullWidth
                    >
                        <OTPInputField
                            verificationCode={mfaVerificationCode}
                            onChangeAuthForm={(e) => setMfaVerificationCode(e.target.value)}
                            name="mfaVerificationCode"
                            disableKeeper={true}
                            disabled={disabled}
                        />
                    </WithActions>
                </div>
            </Box>

            <WithActions forceSpacing={true} isButton={true}>
                <ButtonPrimary type="submit" disabled={disabled || isLoading}>
                    {isLoading ? 'checking...' : 'Submit'}
                </ButtonPrimary>
            </WithActions>

        </Box>
    );
};

export default VerifyMFAForm;
